import React, { useCallback, useMemo, useState } from "react"
import { Box } from "./Box"
import { Stack } from "./Stack"
import { Text } from "./Text"

interface ShowMoreProps<T> {
	data: T[]
	slice?: number
	displayKey?: T extends object ? keyof T : never
	showMoreText?: string
	showLessText?: string
	renderItem?: (item: T, index: number) => React.ReactNode
	gap?: React.ComponentProps<typeof Stack>["gap"]
}
export function ContentToggle<T>({
	data,
	slice = 2,
	displayKey,
	showMoreText = "View More",
	showLessText = "View Less",
	renderItem,
	gap = "1",
}: ShowMoreProps<T>) {
	const [showAll, setShowAll] = useState(false)

	const isEmpty = (value: unknown): boolean =>
		value === null ||
		value === undefined ||
		(typeof value === "string" && value.trim() === "")

	const getDefaultDisplayKey = useCallback(
		(item: Record<string, unknown>): string | undefined => {
			const fallbackKeys = ["name", "title", "label", "value", "id"]
			return fallbackKeys.find((key) => !isEmpty(item[key]))
		},
		[]
	)

	const isValidItem = useCallback(
		(item: T): boolean => {
			if (renderItem || React.isValidElement(item)) return true
			if (typeof item === "object" && item !== null) {
				const obj = item as Record<string, unknown>
				if (displayKey && !isEmpty(obj[displayKey as string])) return true
				return !!getDefaultDisplayKey(obj)
			}
			return !isEmpty(item)
		},
		[renderItem, displayKey, getDefaultDisplayKey]
	)

	// filtering the data to only include valid items
	const validData = useMemo(() => data.filter(isValidItem), [data, isValidItem])
	const displayData = useMemo(
		() => (showAll ? validData : validData.slice(0, slice)),
		[validData, showAll, slice]
	)
	const renderContent = (item: T, index: number): React.ReactNode => {
		if (renderItem) return renderItem(item, index)
		if (React.isValidElement(item)) return item

		if (typeof item === "object" && item !== null) {
			const obj = item as Record<string, unknown>
			const key =
				displayKey && obj[displayKey as string] !== undefined
					? displayKey
					: getDefaultDisplayKey(obj)
			return String(key ? obj[key as string] : JSON.stringify(item))
		}

		return String(item)
	}

	const toggleShowAll = () => setShowAll((prev) => !prev)

	return (
		<Box>
			<Stack gap={gap}>
				{displayData.map((item, index) => (
					<Box key={index}>{renderContent(item, index)}</Box>
				))}
			</Stack>
			{validData.length > slice && (
				<Box onClick={toggleShowAll}>
					<Text color="accent" cursor="pointer" textDecoration="underline">
						{showAll ? showLessText : showMoreText}
					</Text>
				</Box>
			)}
		</Box>
	)
}
